/* eslint-disable no-restricted-syntax */
import { atomWithStorage } from 'jotai/utils';
import { useAtom } from 'jotai';

const initialTooltipStatus = {
    activateOfferTooltip: {
        isSeenByUser: false,
        showTooltip: false,
        priority: 1,
    },
    storeCouponsTooltip: {
        isSeenByUser: false,
        showTooltip: false,
        priority: 2,
    },
    earnPointsTooltip: {
        isSeenByUser: false,
        showTooltip: false,
        priority: 3,
    },
    pointsHistoryTooltip: {
        isSeenByUser: false,
        showTooltip: false,
        priority: 4,
    },
    categoriesTooltip: {
        isSeenByUser: false,
        showTooltip: false,
        priority: 5,
    },
    scanRewardNumberTooltip: {
        isSeenByUser: false,
        showTooltip: false,
        priority: 6,
    },
};

type TooltipStatus = typeof initialTooltipStatus;
type TooltipKeys = keyof TooltipStatus;

const tooltipStatusAtom = atomWithStorage(
    'persistent_tooltipStatusAtom',
    initialTooltipStatus
);

export const showUserTooltipsAtom = atomWithStorage(
    'persistent_showUserTooltipsAtom',
    false
);

function useErTooltips() {
    const [tooltipStatus, setTooltipStatus] = useAtom(tooltipStatusAtom);
    const [showUserTooltips] = useAtom(showUserTooltipsAtom);

    // Use this function to check if a particular tooltip should be shown to the user by passing the key as an argument.
    // Any additional conditions to check if tooltip should show should be handled separately.
    const showTooltip = (key: TooltipKeys) => {
        const result =
            showUserTooltips &&
            tooltipStatus[key].showTooltip &&
            !tooltipStatus[key].isSeenByUser;
        if (!result) {
            return result;
        }
        const tooltips = Object.values(tooltipStatus).sort(
            (tooltip1, tooltip2) => tooltip1.priority - tooltip2.priority
        );
        const tooltipPriority = tooltipStatus[key].priority;

        for (const tooltip of tooltips) {
            if (tooltip.priority === tooltipPriority) {
                break;
            }
            if (!tooltip.isSeenByUser && tooltip.showTooltip) {
                return false;
            }
        }
        return result;
    };

    const setShowTooltip = (key: TooltipKeys) => {
        setTooltipStatus((currentStatus) => {
            const newObj = { ...currentStatus[key], showTooltip: true };
            return {
                ...currentStatus,
                [key]: newObj,
            };
        });
    };

    const setTooltipStatusAsSeen = (key: TooltipKeys) => {
        setTooltipStatus((currentStatus) => {
            const newObj = { ...currentStatus[key], isSeenByUser: true };
            return {
                ...currentStatus,
                [key]: newObj,
            };
        });
    };

    const isTooltipSeen = (key: TooltipKeys) => tooltipStatus[key].isSeenByUser;

    const resetTooltipStatus = () => {
        setTooltipStatus(initialTooltipStatus);
    };

    return {
        showTooltip,
        setShowTooltip,
        setTooltipStatusAsSeen,
        isTooltipSeen,
        resetTooltipStatus,
    };
}

export default useErTooltips;
